/**
 * The .rlm pack container.
 *
 * Layout, little-endian throughout:
 *
 *   "RLMP"            4 bytes  magic
 *   version           u16      currently 1
 *   flags             u16      bit 0 set means the payload is encrypted
 *   manifestLen       u32
 *   manifest          utf8 JSON, ALWAYS plaintext
 *   payload           if encrypted: iv(12) || AES-GCM ciphertext
 *                     otherwise the section blob directly
 *
 *   section blob:
 *     count           u32
 *     per section:    nameLen u8 | name utf8 | byteLen u32 | bytes
 *
 * The manifest stays in the clear on purpose. The loader must know the
 * embedder id and dimension before it can decide whether it is even able to
 * use the pack, and refusing an embedder mismatch loudly is worth more than
 * hiding the fact that a pack exists. What the encryption protects is the
 * facts themselves.
 *
 * On honesty: the key ships in the client, so this is casual-proof, not
 * cryptographer-proof. It makes conversation the sane way in. The eggs are
 * the part that survives a determined key extraction, because canary phrases
 * are stored only as hashes and you cannot grep for a phrase that was never
 * written down.
 *
 * Pure and isomorphic. WebCrypto and TextEncoder are globals in both the
 * browser and Node 22, so the exporter writes with the same code the page
 * reads with.
 */

export const MAGIC = "RLMP";
export const VERSION = 1;
const FLAG_ENCRYPTED = 1;
const IV_BYTES = 12;

export type PackManifest = {
  /** Short id, e.g. "spark-kit". */
  id: string;
  /** Full persona name, e.g. "The Mind's Spark Expert". */
  persona: string;
  /** Prepended to the system prompt. Lives in the pack so the pack is the identity. */
  preamble: string;
  /** HF id of the embedder that produced the vectors. The loader refuses a mismatch. */
  embedder: string;
  dim: number;
  /**
   * Task prefixes the embedder was trained with, carried in the pack so the
   * page cannot get them wrong.
   *
   * This is the structural fix for the failure that shaped the whole design:
   * nomic wants "search_document:" and "search_query:", embeddinggemma wants
   * its own pair, and applying the wrong one degrades recall without raising
   * anything. A silent quality loss is the worst failure class we have, so the
   * pack declares its own contract instead of the app remembering it.
   */
  docPrefix: string;
  queryPrefix: string;
  factCount: number;
  builtAt: string;
  /** Free text shown in the pack picker. */
  note?: string;
  /** Source cycle numbers, carried through from the lattice for provenance. */
  memoryCycle?: number;
  dreamCycle?: number;
};

export type Sections = Record<string, Uint8Array>;

const enc = new TextEncoder();
const dec = new TextDecoder();

function u8(...parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function encodeSections(sections: Sections): Uint8Array {
  const names = Object.keys(sections).sort();
  const chunks: Uint8Array[] = [];
  const head = new Uint8Array(4);
  new DataView(head.buffer).setUint32(0, names.length, true);
  chunks.push(head);

  for (const name of names) {
    const nameBytes = enc.encode(name);
    if (nameBytes.length > 255) throw new Error(`section name too long: ${name}`);
    const body = sections[name];
    const hdr = new Uint8Array(1 + nameBytes.length + 4);
    const dv = new DataView(hdr.buffer);
    dv.setUint8(0, nameBytes.length);
    hdr.set(nameBytes, 1);
    dv.setUint32(1 + nameBytes.length, body.length, true);
    chunks.push(hdr, body);
  }
  return u8(...chunks);
}

export function decodeSections(bytes: Uint8Array): Sections {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 0;
  const count = dv.getUint32(o, true);
  o += 4;

  const out: Sections = {};
  for (let i = 0; i < count; i++) {
    if (o + 1 > bytes.length) throw new Error("pack truncated in a section header");
    const nameLen = dv.getUint8(o);
    o += 1;
    const name = dec.decode(bytes.subarray(o, o + nameLen));
    o += nameLen;
    const byteLen = dv.getUint32(o, true);
    o += 4;
    if (o + byteLen > bytes.length) throw new Error(`pack truncated in section "${name}"`);
    // Copy rather than subarray so callers can transfer or retain sections
    // without pinning the whole pack buffer in memory.
    out[name] = bytes.slice(o, o + byteLen);
    o += byteLen;
  }
  return out;
}

/** Derive an AES-GCM key from a passphrase. Deterministic, so the exporter and the page agree. */
export async function deriveKey(passphrase: string, salt: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode(salt), iterations: 100_000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encodePack(manifest: PackManifest, sections: Sections, key?: CryptoKey): Promise<Uint8Array> {
  const manifestBytes = enc.encode(JSON.stringify(manifest));
  const blob = encodeSections(sections);

  let payload: Uint8Array;
  let flags = 0;
  if (key) {
    flags |= FLAG_ENCRYPTED;
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, blob as BufferSource));
    payload = u8(iv, ct);
  } else {
    payload = blob;
  }

  const head = new Uint8Array(4 + 2 + 2 + 4);
  head.set(enc.encode(MAGIC), 0);
  const dv = new DataView(head.buffer);
  dv.setUint16(4, VERSION, true);
  dv.setUint16(6, flags, true);
  dv.setUint32(8, manifestBytes.length, true);

  return u8(head, manifestBytes, payload);
}

export type DecodedPack = { manifest: PackManifest; sections: Sections; encrypted: boolean };

/** Read only the manifest. Cheap, and lets the picker describe a pack before decrypting it. */
export function peekManifest(bytes: Uint8Array): { manifest: PackManifest; encrypted: boolean } {
  if (bytes.length < 12) throw new Error("not a pack: too short");
  if (dec.decode(bytes.subarray(0, 4)) !== MAGIC) throw new Error("not a pack: bad magic");
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = dv.getUint16(4, true);
  if (version !== VERSION) throw new Error(`unsupported pack version ${version}, expected ${VERSION}`);
  const flags = dv.getUint16(6, true);
  const manifestLen = dv.getUint32(8, true);
  if (12 + manifestLen > bytes.length) throw new Error("pack truncated in the manifest");
  const manifest = JSON.parse(dec.decode(bytes.subarray(12, 12 + manifestLen))) as PackManifest;
  return { manifest, encrypted: (flags & FLAG_ENCRYPTED) !== 0 };
}

export async function decodePack(bytes: Uint8Array, key?: CryptoKey): Promise<DecodedPack> {
  const { manifest, encrypted } = peekManifest(bytes);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const manifestLen = dv.getUint32(8, true);
  const payload = bytes.subarray(12 + manifestLen);

  let blob: Uint8Array;
  if (encrypted) {
    if (!key) throw new Error("this pack is encrypted and no key was supplied");
    const iv = payload.subarray(0, IV_BYTES);
    const ct = payload.subarray(IV_BYTES);
    try {
      blob = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct as BufferSource));
    } catch {
      // AES-GCM authenticates, so a wrong key is a clean failure rather than garbage.
      throw new Error("pack failed to decrypt: wrong key or the file was modified");
    }
  } else {
    blob = payload;
  }

  return { manifest, sections: decodeSections(blob), encrypted };
}

/* ---- typed helpers for the sections we actually use ---- */

export const toJson = <T>(b: Uint8Array): T => JSON.parse(dec.decode(b)) as T;
export const fromJson = (v: unknown): Uint8Array => enc.encode(JSON.stringify(v));

export function toFloat32(b: Uint8Array): Float32Array {
  // A section may be copied to a non-4-aligned offset, so go through the bytes.
  const out = new Float32Array(b.length / 4);
  new Uint8Array(out.buffer).set(b);
  return out;
}

export function fromFloat32(v: Float32Array): Uint8Array {
  return new Uint8Array(v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength));
}
